
import { useState, useEffect } from 'react';
import { supabase } from '../../../supabaseClient';

interface GoLiveModalProps {
  onClose: () => void;
  onSuccess?: () => void;
}

export default function GoLiveModal({ onClose, onSuccess }: GoLiveModalProps) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'ranked',
    streamUrl: '',
    scheduleNow: true,
    scheduleDate: '',
    scheduleTime: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<any>(null);

  useEffect(() => {
    checkUser();
  }, []);

  const checkUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setUser(user);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      if (!user) {
        setError('You must be logged in to start a stream');
        setIsSubmitting(false);
        return;
      }

      if (!formData.title.trim()) {
        setError('Please enter a stream title');
        setIsSubmitting(false);
        return;
      }

      // Calculate scheduled time if scheduling for later
      let scheduledFor = null;
      if (!formData.scheduleNow && formData.scheduleDate && formData.scheduleTime) {
        scheduledFor = new Date(`${formData.scheduleDate}T${formData.scheduleTime}`).toISOString();
      }

      // Generate a placeholder thumbnail based on category
      const thumbnailUrl = `https://public.readdy.ai/ai/img_res/9ca248dee3ba1dfd1eeb913b3de7b3d2.jpg$%7BformData.category%7D%20gaming%20stream%20thumbnail%20with%20professional%20esports%20overlay%2C%20live%20streaming%20setup%20with%20red%20and%20black%20theme%2C%20competitive%20gaming%20atmosphere&width=640&height=360&seq=stream-${Date.now()}&orientation=landscape`;

      // Insert stream into database
      const { error: insertError } = await supabase
        .from('streams')
        .insert({
          user_id: user.id,
          streamer_id: user.id,
          title: formData.title,
          description: formData.description || null,
          stream_url: formData.streamUrl || null,
          thumbnail_url: thumbnailUrl,
          is_live: formData.scheduleNow,
          viewer_count: 0,
          started_at: formData.scheduleNow ? new Date().toISOString() : null,
          scheduled_for: scheduledFor,
          category: formData.category,
          tags: [formData.category, 'brawlhalla']
        });

      if (insertError) {
        throw new Error(`Failed to create stream: ${insertError.message}`);
      }

      setSubmitSuccess(true);
      
      setTimeout(() => {
        onSuccess?.();
        onClose();
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-700 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-white flex items-center">
            <i className="ri-live-line text-red-600 mr-3"></i>
            Go Live
          </h2>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors cursor-pointer w-10 h-10 flex items-center justify-center"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        {submitSuccess ? (
          <div className="p-12 text-center">
            <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-check-line text-white text-4xl"></i>
            </div>
            <h3 className="text-2xl font-bold text-white mb-3">
              {formData.scheduleNow ? "You're Live!" : 'Stream Scheduled!'}
            </h3>
            <p className="text-gray-400">
              {formData.scheduleNow 
                ? 'Your stream is now live. Start broadcasting to your audience!'
                : 'Your stream has been scheduled. We\'ll notify your subscribers!'}
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6">
            {error && (
              <div className="mb-6 bg-red-600/20 border border-red-600 rounded-lg p-4 flex items-center">
                <i className="ri-error-warning-line text-red-500 text-xl mr-3"></i>
                <p className="text-red-400">{error}</p>
              </div>
            )}

            <div className="mb-6">
              <label className="block text-white font-semibold mb-2">
                Stream Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Ranked Grind to Top 100"
                className="w-full bg-gray-900 text-white px-4 py-3 rounded-lg border border-gray-700 focus:border-red-600 focus:outline-none text-sm"
              />
            </div>

            <div className="mb-6">
              <label className="block text-white font-semibold mb-2">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                required
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full bg-gray-900 text-white px-4 py-3 rounded-lg border border-gray-700 focus:border-red-600 focus:outline-none pr-8 text-sm cursor-pointer"
              >
                <option value="ranked">Ranked Matches</option>
                <option value="tournament">Tournament</option>
                <option value="training">Training Session</option>
                <option value="casual">Casual Play</option>
                <option value="tutorial">Tutorial</option>
                <option value="community">Community Event</option>
              </select>
            </div>

            <div className="mb-6">
              <label className="block text-white font-semibold mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Tell viewers what to expect from your stream..."
                maxLength={500}
                rows={4}
                className="w-full bg-gray-900 text-white px-4 py-3 rounded-lg border border-gray-700 focus:border-red-600 focus:outline-none resize-none text-sm"
              />
              <div className="text-right text-gray-500 text-sm mt-1">
                {formData.description.length}/500
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-white font-semibold mb-3">
                When to Stream?
              </label>
              <div className="flex gap-4 mb-4">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, scheduleNow: true })}
                  className={`flex-1 py-3 rounded-lg font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    formData.scheduleNow
                      ? 'bg-red-600 text-white'
                      : 'bg-gray-900 text-gray-400 border border-gray-700'
                  }`}
                >
                  <i className="ri-live-line mr-2"></i>
                  Go Live Now
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, scheduleNow: false })}
                  className={`flex-1 py-3 rounded-lg font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    !formData.scheduleNow
                      ? 'bg-red-600 text-white'
                      : 'bg-gray-900 text-gray-400 border border-gray-700'
                  }`}
                >
                  <i className="ri-calendar-line mr-2"></i>
                  Schedule Later
                </button>
              </div>

              {!formData.scheduleNow && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-400 text-sm mb-2">Date</label>
                    <input
                      type="date"
                      required={!formData.scheduleNow}
                      value={formData.scheduleDate}
                      onChange={(e) => setFormData({ ...formData, scheduleDate: e.target.value })}
                      className="w-full bg-gray-900 text-white px-4 py-3 rounded-lg border border-gray-700 focus:border-red-600 focus:outline-none text-sm cursor-pointer"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-2">Time</label>
                    <input
                      type="time"
                      required={!formData.scheduleNow}
                      value={formData.scheduleTime}
                      onChange={(e) => setFormData({ ...formData, scheduleTime: e.target.value })}
                      className="w-full bg-gray-900 text-white px-4 py-3 rounded-lg border border-gray-700 focus:border-red-600 focus:outline-none text-sm cursor-pointer"
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="mb-6">
              <label className="block text-white font-semibold mb-2">
                Stream URL (Optional)
              </label>
              <input
                type="url"
                value={formData.streamUrl}
                onChange={(e) => setFormData({ ...formData, streamUrl: e.target.value })}
                placeholder="e.g., https://twitch.tv/yourusername or RTMP URL"
                className="w-full bg-gray-900 text-white px-4 py-3 rounded-lg border border-gray-700 focus:border-red-600 focus:outline-none text-sm"
              />
              <p className="text-gray-500 text-sm mt-2">
                Enter your Twitch, YouTube, or custom RTMP stream URL
              </p>
            </div>

            <div className="bg-gray-900 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <i className="ri-information-line text-red-400 text-xl mr-3 mt-1"></i>
                <div>
                  <p className="text-white font-semibold mb-1">Streaming Guidelines</p>
                  <ul className="text-gray-400 text-sm space-y-1">
                    <li>• Maintain respectful communication with viewers</li>
                    <li>• No cheating, exploits, or inappropriate content</li>
                    <li>• Stream quality: minimum 720p recommended</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-gray-700 hover:bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Setting Up...
                  </>
                ) : (
                  <>
                    <i className="ri-live-line mr-2"></i>
                    {formData.scheduleNow ? 'Start Stream' : 'Schedule Stream'}
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
